<template>
  <transition name="slide-fade">
    <div
      v-if="isOpen"
      class="absolute right-0 mt-3 w-[420px] bg-white dark:bg-gray-900 rounded-2xl shadow-2xl z-50 max-h-[85vh] flex flex-col overflow-hidden"
      ref="dropdownContainer"
    >
      <!-- Modern Header -->
      <div class="relative px-5 py-4 bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 border-b border-gray-100 dark:border-gray-800">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-3">
            <div class="w-8 h-8 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center">
              <i class="fas fa-truck text-red-600 dark:text-red-400 text-xs"></i>
            </div>
            <div>
              <h3 class="font-semibold text-gray-900 dark:text-white text-sm">Canlı Atama</h3>
              <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{{ totalOrdersCount }} aktif sipariş</p>
            </div>
          </div>
          <button
            @click="$emit('close')"
            class="w-8 h-8 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors flex items-center justify-center group"
          >
            <i class="fas fa-times text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 text-sm"></i>
          </button>
        </div>

        <!-- Crisis Alert - More Subtle -->
        <transition name="alert-slide">
          <div v-if="hasCrisisOrders" class="mt-3 px-3 py-2 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-100 dark:border-red-900/30">
            <div class="flex items-center gap-2">
              <span class="relative flex h-2 w-2">
                <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                <span class="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
              </span>
              <p class="text-xs text-red-700 dark:text-red-400 font-medium">
                {{ crisisOrdersCount }} kritik sipariş bekliyor
              </p>
            </div>
          </div>
        </transition>
      </div>
    
      <!-- Order List with Better Scrolling -->
      <div class="flex-1 overflow-y-auto custom-scrollbar">
        <live-assignment-order-list
          :orders="orders"
          :loading="loading"
          @tab-change="(orderId, tab) => $emit('tab-change', orderId, tab)"
          @assign="(orderId, vehicleId) => $emit('assign', orderId, vehicleId)"
          @bulkAssign="(orderId, vehicles) => handleBulkAssign(orderId, vehicles)"
          ref="orderList"
        />
      </div>

      <!-- Modern Footer -->
      <div class="px-5 py-3 bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 border-t border-gray-100 dark:border-gray-800">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
            <i class="far fa-clock text-[10px]"></i>
            <span>{{ formatLastUpdate }}</span>
          </div>
          <button
            @click="$emit('refresh')"
            class="px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all duration-200 flex items-center gap-1.5"
            :disabled="loading"
          >
            <i class="fas fa-sync-alt text-[10px]" :class="{'animate-spin': loading}"></i>
            <span>Yenile</span>
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import LiveAssignmentOrderList from './LiveAssignmentOrderList.vue';

export default {
  name: "LiveAssignmentDropdown",
  components: {
    LiveAssignmentOrderList,
  },
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    orders: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    lastUpdate: {
      type: String,
      default: "-"
    }
  },
  methods: {
    handleBulkAssign(orderId, vehicles) {
      this.$emit('bulkAssign', orderId, vehicles);
    },
    // Tümünü aç ve tümünü kapat fonksiyonlarını doğrudan OrderList'e aktarabilmek için
    expandAllOrders(orderType) {
      if (this.$refs.orderList) {
        const orders = orderType === 'crisis' ? this.crisisOrders : 
                       orderType === 'normal' ? this.normalOrders : this.orders;
                       
        this.$refs.orderList.expandAll(orders);
      }
    },
    collapseAllOrders(orderType) {
      if (this.$refs.orderList) {
        const orders = orderType === 'crisis' ? this.crisisOrders : 
                       orderType === 'normal' ? this.normalOrders : this.orders;
                       
        this.$refs.orderList.collapseAll(orders);
      }
    }
  },
  computed: {
    // Kriz durumu olan siparişleri bul
    crisisOrders() {
      return this.orders.filter(order => order.hasCrisis === true);
    },
    // Kriz durumu olan sipariş sayısı
    crisisOrdersCount() {
      return this.crisisOrders.length;
    },
    // Kriz durumu var mı?
    hasCrisisOrders() {
      return this.crisisOrdersCount > 0;
    },
    // Toplam sipariş sayısı
    totalOrdersCount() {
      return this.orders.length;
    },
    // Format last update time
    formatLastUpdate() {
      if (this.lastUpdate === '-') return 'Güncelleniyor...';
      return `Son güncelleme: ${this.lastUpdate}`;
    }
  }
};
</script>

<style scoped>
/* Modern Custom Scrollbar */
.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: rgba(156, 163, 175, 0.3) transparent;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: rgba(156, 163, 175, 0.3);
  border-radius: 20px;
  border: transparent;
}

.custom-scrollbar:hover::-webkit-scrollbar-thumb {
  background-color: rgba(156, 163, 175, 0.5);
}

/* Smooth Animations */
.slide-fade-enter-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-fade-leave-active {
  transition: all 0.2s cubic-bezier(0.4, 0, 1, 1);
}

.slide-fade-enter-from {
  transform: translateY(-10px) scale(0.95);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateY(-5px) scale(0.98);
  opacity: 0;
}

.alert-slide-enter-active,
.alert-slide-leave-active {
  transition: all 0.2s ease;
}

.alert-slide-enter-from {
  transform: translateY(-10px);
  opacity: 0;
}

.alert-slide-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}

/* Pulse animation for critical items */
@keyframes pulse-red {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.pulse-red {
  animation: pulse-red 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}
</style>